import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Seo } from "@/components/Layout";
import { Overline } from "@/components/Bits";
import AudioButton from "@/components/AudioButton";
import api from "@/lib/api";
import { RotateCw, Check, X, Brain, AlertCircle, Volume2 } from "lucide-react";

export default function Review() {
  const [items, setItems] = useState(null);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [done, setDone] = useState(false);
  const [score, setScore] = useState({ known: 0, review: 0 });

  useEffect(() => { api.get("/review/session").then(({ data }) => setItems(data.items)); }, []);

  const item = items && items[idx];
  const answer = async (known) => {
    setScore((s) => ({ known: s.known + (known ? 1 : 0), review: s.review + (known ? 0 : 1) }));
    try { await api.post("/review/result", { item_id: item.id, correct: known, front: item.front }); } catch (e) { console.error("No se pudo guardar el resultado del repaso:", e); }
    if (idx < items.length - 1) { setIdx(idx + 1); setFlipped(false); }
    else setDone(true);
  };

  if (!items) return <div className="min-h-[50vh] flex items-center justify-center text-muted-foreground">Preparando tu repaso...</div>;

  if (items.length === 0) {
    return (
      <>
        <Seo title="Repaso inteligente" noindex />
        <div className="mx-auto max-w-xl px-4 py-20 text-center">
          <Brain size={40} className="mx-auto text-primary mb-4" />
          <h1 className="font-display font-bold text-2xl">Nada que repasar por ahora</h1>
          <p className="text-muted-foreground mt-2">Completa alguna lección para generar tu sesión de repaso.</p>
          <Link to="/cursos"><Button className="mt-6 rounded-full">Ir a los cursos</Button></Link>
        </div>
      </>
    );
  }

  if (done) {
    return (
      <>
        <Seo title="Repaso completado" noindex />
        <div className="mx-auto max-w-xl px-4 py-20 text-center" data-testid="review-done">
          <div className="inline-flex h-16 w-16 rounded-full bg-primary/10 items-center justify-center mb-4"><Brain className="text-primary" size={28} /></div>
          <h1 className="font-display font-black text-3xl">¡Repaso completado!</h1>
          <p className="text-muted-foreground mt-2">Sabías {score.known} · A reforzar {score.review}</p>
          <div className="mt-6 flex gap-3 justify-center">
            <Button className="rounded-full" onClick={() => { setIdx(0); setFlipped(false); setDone(false); setScore({ known: 0, review: 0 }); api.get("/review/session").then(({ data }) => setItems(data.items)); }}>Repasar otra vez</Button>
            <Link to="/panel"><Button variant="outline" className="rounded-full">Volver al panel</Button></Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Seo title="Repaso inteligente" noindex />
      <div className="mx-auto max-w-xl px-4 sm:px-6 py-10">
        <Overline>Repaso inteligente</Overline>
        <p className="text-sm text-muted-foreground mb-4">Priorizamos tus errores recientes y tu vocabulario.</p>
        <Progress value={(idx / items.length) * 100} className="mb-6" />
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Tarjeta {idx + 1} de {items.length}</p>

        <div onClick={() => setFlipped(!flipped)} data-testid="review-card"
          className="cursor-pointer rounded-lg border border-border bg-card p-8 min-h-[220px] flex flex-col items-center justify-center text-center transition-colors hover:border-primary/40">
          {item.priority === "alta" && <span className="mb-3 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400"><AlertCircle size={11} />A reforzar</span>}
          {item.type === "vocab" && !flipped && (
            <div className="flex items-center gap-2">
              <p className="font-display font-black text-3xl">{item.front}</p>
              <AudioButton text={item.front} size={20} />
            </div>
          )}
          {!(item.type === "vocab" && !flipped) && (
            <p className={`${flipped ? "text-lg" : "font-display font-bold text-xl"} leading-relaxed`}>{flipped ? item.back : item.front}</p>
          )}
          {!flipped && <p className="mt-4 text-xs text-muted-foreground flex items-center gap-1"><RotateCw size={12} />Toca para ver la respuesta</p>}
        </div>

        {flipped ? (
          <div className="mt-6 grid grid-cols-2 gap-3">
            <Button variant="outline" className="rounded-full" onClick={() => answer(false)} data-testid="review-hard"><X size={16} className="mr-1" />A repasar</Button>
            <Button className="rounded-full" onClick={() => answer(true)} data-testid="review-known"><Check size={16} className="mr-1" />Lo sabía</Button>
          </div>
        ) : (
          <Button variant="outline" className="mt-6 w-full rounded-full" onClick={() => setFlipped(true)} data-testid="review-flip">Ver respuesta</Button>
        )}
      </div>
    </>
  );
}
